/**
 * InboxThreadPane — full email reading pane with optional thread context.
 *
 * Fetches the primary email (with lazy body load) and, if a providerThreadId
 * exists, also fetches all sibling messages in the thread.  Earlier messages
 * show as collapsible cards; the focused email is always expanded.
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Archive, Flag, LinkSimple, ArrowBendUpLeft, ArrowBendDoubleUpLeft, ArrowBendUpRight,
  CaretDown, CaretUp, Paperclip, DownloadSimple, Printer, FolderSimplePlus,
} from '@phosphor-icons/react';
import { fetchEmail, getThreadMessages, downloadAttachment, saveAttachmentToMatter, type EmailDetail, type ThreadEmail } from '@/lib/inboxApi';
import { ReplyPane } from './ReplyPane';
import { SafeEmailHtml } from './SafeEmailHtml';
import { cn } from '@/lib/utils';
import DOMPurify from 'dompurify';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

interface Props {
  emailId: string;
  onArchive: () => void;
  onFollowUp: () => void;
  onLinkMatter: () => void;
  /**
   * Detach this email from its matter. Optional so the search page — which mounts this pane
   * without the inbox's mutation wiring — simply renders no control rather than a dead button.
   */
  onUnlinkMatter?: () => void;
  onCreateTask?: (matterId: string, matterName: string) => void;
  onCreateHearingNote?: (matterId: string, matterName: string) => void;
  onLogTime?: (matterId: string, matterName: string) => void;
  onGenerateFeeEntry?: (matterId: string, matterName: string) => void;
  onSaveToDocuments?: (matterId: string, matterName: string) => void;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function senderName(addr: string): string {
  const m = addr.match(/^([^<]+)<[^>]+>$/);
  return m ? m[1]!.trim() : addr;
}

function fileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Single message card (used for thread siblings) ────────────

function ThreadMessageCard({
  msg, isExpanded, onToggle,
}: {
  msg: ThreadEmail;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{
      border: '1px solid var(--border-default)',
      borderRadius: 8,
      marginBottom: 8,
      overflow: 'hidden',
    }}>
      {/* Collapsed / expanded header */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isExpanded ? '14px 20px 10px' : '10px 20px',
          cursor: 'pointer',
          background: 'var(--surface-card)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Avatar */}
          <div style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: '#1e3a5f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>
              {senderName(msg.fromAddress).charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div style={{
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}>
              {senderName(msg.fromAddress)}
            </div>
            {!isExpanded && msg.snippet && (
              <div style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 12,
                color: 'var(--text-subtle)',
                marginTop: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 340,
              }}>
                {msg.snippet}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 12,
            color: 'var(--text-subtle)',
          }}>
            {fmtDate(msg.receivedAt ?? msg.sentAt)}
          </span>
          {isExpanded ? <CaretUp size={14} color="#9ca3af" /> : <CaretDown size={14} color="#9ca3af" />}
        </div>
      </div>

      {/* Expanded: to/cc + body + attachments */}
      {isExpanded && (
        <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--border-subtle)' }}>
          {/* To / CC */}
          <div style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 12,
            color: 'var(--text-muted)',
            marginTop: 8,
            marginBottom: 12,
          }}>
            <span style={{ color: 'var(--text-subtle)' }}>To </span>
            {msg.toAddresses.join(', ')}
            {msg.ccAddresses.length > 0 && (
              <>
                <span style={{ color: 'var(--text-subtle)', marginLeft: 12 }}>CC </span>
                {msg.ccAddresses.join(', ')}
              </>
            )}
          </div>

          {/* Body */}
          {msg.bodyFetched ? (
            msg.bodyHtml ? (
              <SafeEmailHtml html={msg.bodyHtml} />
            ) : (
              <pre style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 14,
                color: 'var(--text-primary)',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                margin: 0,
              }}>
                {msg.bodyText}
              </pre>
            )
          ) : (
            <div style={{ color: 'var(--text-subtle)', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14 }}>
              Body not yet loaded
            </div>
          )}

          {/* Attachments */}
          {msg.attachments.length > 0 && (
            <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {msg.attachments.map((att) => (
                <div key={att.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', background: 'var(--surface)',
                  border: '1px solid var(--border-default)', borderRadius: 6,
                }}>
                  <Paperclip size={12} color="#6b7280" />
                  <div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-primary)' }}>
                      {att.filename}
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-subtle)' }}>
                      {fileSize(att.sizeBytes)}
                    </div>
                  </div>
                  <button
                    onClick={() => downloadAttachment(msg.id, att.id, att.filename).catch(() => toast.error('Download failed'))}
                    title="Download"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', padding: 2 }}
                  >
                    <DownloadSimple size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Primary email body (always expanded) ─────────────────────

function PrimaryEmailBody({ email, onSaveAttachmentToMatter, savingAttId }: {
  email: EmailDetail;
  onSaveAttachmentToMatter?: (attId: string, filename: string) => void;
  savingAttId?: string | null;
}) {
  return (
    <div style={{ padding: '0 4px' }}>
      {/* From / To / CC */}
      <div style={{
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontSize: 13,
        color: 'var(--text-muted)',
        marginBottom: 16,
        lineHeight: 1.8,
      }}>
        <div>
          <span style={{ color: 'var(--text-subtle)', display: 'inline-block', minWidth: 32 }}>From</span>
          <span style={{ color: 'var(--text-primary)', marginLeft: 8 }}>{email.fromAddress}</span>
        </div>
        <div>
          <span style={{ color: 'var(--text-subtle)', display: 'inline-block', minWidth: 32 }}>To</span>
          <span style={{ color: 'var(--text-primary)', marginLeft: 8 }}>{email.toAddresses.join(', ')}</span>
        </div>
        {email.ccAddresses.length > 0 && (
          <div>
            <span style={{ color: 'var(--text-subtle)', display: 'inline-block', minWidth: 32 }}>CC</span>
            <span style={{ color: 'var(--text-primary)', marginLeft: 8 }}>{email.ccAddresses.join(', ')}</span>
          </div>
        )}
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-subtle)' }}>
          {fmtDate(email.receivedAt ?? email.sentAt)}
        </div>
      </div>

      {/* Body */}
      {email.bodyFetched ? (
        email.bodyHtml ? (
          <SafeEmailHtml html={email.bodyHtml} />
        ) : (
          <pre style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 14,
            color: 'var(--text-primary)',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            margin: 0,
          }}>
            {email.bodyText || '(empty)'}
          </pre>
        )
      ) : (
        <div style={{ color: 'var(--text-subtle)', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, padding: '16px 0' }}>
          Loading email body…
        </div>
      )}

      {/* Attachments */}
      {email.attachments.length > 0 && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)', marginBottom: 8 }}>
            Attachments ({email.attachments.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {email.attachments.map((att) => (
              <div key={att.id} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 10px', background: 'var(--surface)',
                border: '1px solid var(--border-default)', borderRadius: 6,
              }}>
                <Paperclip size={12} color="#6b7280" />
                <div>
                  <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-primary)' }}>
                    {att.filename}
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-subtle)' }}>
                    {fileSize(att.sizeBytes)}
                  </div>
                </div>
                {email.matter && onSaveAttachmentToMatter && (
                  <button
                    onClick={() => onSaveAttachmentToMatter(att.id, att.filename)}
                    disabled={savingAttId === att.id}
                    title={`Save to ${email.matter.caseNumber}`}
                    style={{ background: 'none', border: 'none', cursor: savingAttId === att.id ? 'wait' : 'pointer', color: '#2563eb', display: 'flex', alignItems: 'center', padding: 2, opacity: savingAttId === att.id ? 0.5 : 1 }}
                  >
                    <FolderSimplePlus size={14} />
                  </button>
                )}
                <button
                  onClick={() => downloadAttachment(email.id, att.id, att.filename).catch(() => toast.error('Download failed'))}
                  title="Download"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', padding: 2 }}
                >
                  <DownloadSimple size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main InboxThreadPane ──────────────────────────────────────

export function InboxThreadPane({ emailId, onArchive, onFollowUp, onLinkMatter, onUnlinkMatter, onCreateTask, onCreateHearingNote, onLogTime, onGenerateFeeEntry, onSaveToDocuments }: Props) {
  const [replyMode, setReplyMode] = useState<'reply' | 'reply-all' | 'forward' | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: email, isLoading } = useQuery({
    queryKey: ['inbox-email', emailId],
    queryFn: () => fetchEmail(emailId),
  });

  const { data: threadMessages = [] } = useQuery({
    queryKey: ['inbox-thread', email?.providerThreadId],
    queryFn: () => getThreadMessages(email!.providerThreadId!),
    enabled: !!email?.providerThreadId,
  });

  // Save-attachment-to-matter
  const [savingAttId, setSavingAttId] = useState<string | null>(null);
  const saveAttMutation = useMutation({
    mutationFn: ({ attId }: { attId: string; filename: string }) =>
      saveAttachmentToMatter(emailId, attId, email!.matter!.id),
    onMutate: ({ attId }) => setSavingAttId(attId),
    onSuccess: (_d, { filename }) => toast.success(`"${filename}" saved to ${email!.matter!.caseNumber}`),
    onError: () => toast.error('Could not save to matter'),
    onSettled: () => setSavingAttId(null),
  });
  function handleSaveAttachment(attId: string, filename: string) {
    if (!email?.matter) { toast.error('Link this email to a matter first'); return; }
    saveAttMutation.mutate({ attId, filename });
  }

  function handlePrint() {
    if (!email) return;
    const win = window.open('', '_blank', 'width=820,height=900');
    if (!win) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
    const body = email.bodyHtml
      ? DOMPurify.sanitize(email.bodyHtml)
      : `<pre style="white-space:pre-wrap;font-family:inherit">${escapeHtml(email.bodyText ?? '')}</pre>`;
    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(email.subject || '(no subject)')}</title>`
      + `<style>body{font-family:'IBM Plex Sans',Arial,sans-serif;color:#111;padding:32px;line-height:1.6}`
      + `.h{font-size:20px;font-weight:600;margin:0 0 12px}.meta{font-size:12px;color:#555;margin-bottom:4px}`
      + `hr{border:none;border-top:1px solid #ddd;margin:16px 0}</style></head><body>`
      + `<div class="h">${escapeHtml(email.subject || '(no subject)')}</div>`
      + `<div class="meta"><b>From:</b> ${escapeHtml(email.fromAddress)}</div>`
      + `<div class="meta"><b>To:</b> ${escapeHtml(email.toAddresses.join(', '))}</div>`
      + (email.ccAddresses.length ? `<div class="meta"><b>CC:</b> ${escapeHtml(email.ccAddresses.join(', '))}</div>` : '')
      + `<div class="meta"><b>Date:</b> ${escapeHtml(fmtDate(email.receivedAt ?? email.sentAt))}</div><hr/>`
      + `${body}</body></html>`,
    );
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  }

  // Show thread view when there are multiple messages
  const siblings = threadMessages.filter((m) => m.id !== emailId);
  const hasThread = siblings.length > 0;

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: 'var(--text-subtle)' }}>
          Loading…
        </span>
      </div>
    );
  }

  if (!email) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Top action bar */}
      <div style={{
        padding: '10px 24px',
        borderBottom: '1px solid var(--border-default)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
      }}>
        <ActionBtn title="Archive" onClick={onArchive}>
          <Archive size={14} />
          Archive
        </ActionBtn>
        <ActionBtn title={email.hasReplyFlag ? 'Remove follow-up' : 'Follow up'} onClick={onFollowUp} active={email.hasReplyFlag}>
          <Flag size={14} weight={email.hasReplyFlag ? 'fill' : 'regular'} />
          {email.hasReplyFlag ? 'Following up' : 'Follow up'}
        </ActionBtn>
        <ActionBtn title="Link to matter" onClick={onLinkMatter}>
          <LinkSimple size={14} />
          {email.matter ? email.matter.caseNumber : 'Link matter'}
        </ActionBtn>
        <ActionBtn title="Print" onClick={handlePrint}>
          <Printer size={14} />
          Print
        </ActionBtn>
        <div style={{ flex: 1 }} />
        <ActionBtn title="Reply" onClick={() => setReplyMode('reply')} primary>
          <ArrowBendUpLeft size={14} />
          Reply
        </ActionBtn>
        <ActionBtn title="Reply All" onClick={() => setReplyMode('reply-all')}>
          <ArrowBendDoubleUpLeft size={14} />
          Reply All
        </ActionBtn>
        <ActionBtn title="Forward" onClick={() => setReplyMode('forward')}>
          <ArrowBendUpRight size={14} />
          Forward
        </ActionBtn>
      </div>

      {/* Subject */}
      <div style={{
        padding: '16px 24px 12px',
        borderBottom: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}>
        <h2 style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          fontSize: 20,
          fontWeight: 600,
          color: 'var(--text-primary)',
          margin: 0,
          lineHeight: 1.3,
        }}>
          {email.subject || '(no subject)'}
        </h2>
        {hasThread && (
          <div style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 12,
            color: 'var(--text-subtle)',
            marginTop: 4,
          }}>
            {threadMessages.length} messages in thread
          </div>
        )}
      </div>

      {/* Provider sync status — never let the barrister wonder if a change reached their mailbox */}
      {email.syncStatus && (
        <div style={{
          padding: '6px 24px', flexShrink: 0,
          fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12,
          display: 'flex', alignItems: 'center', gap: 8,
          background: email.syncStatus === 'failed' ? '#7c2d12' : '#0f1f3d',
          color: email.syncStatus === 'failed' ? '#fecaca' : '#93c5fd',
        }}>
          {email.syncStatus === 'failed'
            ? '⚠ This change could not be synced to your mailbox — it will keep retrying.'
            : '◷ Syncing this change to your mailbox…'}
        </div>
      )}

      {/* Scroll area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
        {hasThread ? (
          <>
            {/* Thread siblings (ordered earliest first) */}
            {threadMessages
              .filter((m) => m.id !== emailId)
              .map((msg) => (
                <ThreadMessageCard
                  key={msg.id}
                  msg={msg}
                  isExpanded={expandedIds.has(msg.id)}
                  onToggle={() => toggleExpand(msg.id)}
                />
              ))}

            {/* Primary email — always expanded, at bottom (latest) */}
            <div style={{
              border: '1px solid #1A73E8',
              borderRadius: 8,
              overflow: 'hidden',
              marginBottom: 8,
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '14px 20px 12px',
                borderBottom: '1px solid var(--border-subtle)',
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#1e3a5f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 10,
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>
                    {senderName(email.fromAddress).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}>
                    {senderName(email.fromAddress)}
                  </div>
                  <div style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    fontSize: 12,
                    color: 'var(--text-subtle)',
                  }}>
                    {fmtDate(email.receivedAt ?? email.sentAt)}
                  </div>
                </div>
              </div>
              <div style={{ padding: '16px 20px' }}>
                <PrimaryEmailBody email={email} onSaveAttachmentToMatter={handleSaveAttachment} savingAttId={savingAttId} />
              </div>
            </div>
          </>
        ) : (
          <PrimaryEmailBody email={email} onSaveAttachmentToMatter={handleSaveAttachment} savingAttId={savingAttId} />
        )}

        {/* Matter link bar */}
        {email.matter ? (
          <div style={{
            marginTop: 20,
            padding: '10px 14px',
            background: 'var(--surface-selected)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: '#1e40af' }}>
              ⬡ {email.matter.caseNumber} — {email.matter.caseName}
            </span>
            {/* The bar already SAYS the email is linked; the word "Linked" beside it was inert
                text where the only useful action belongs — and the endpoint to detach it has
                existed, uncalled, the whole time. Mirrors the `suggested` branch below, which
                puts its action ("Accept suggestion") in exactly this position. */}
            {onUnlinkMatter ? (
              <button
                onClick={onUnlinkMatter}
                aria-label="Unlink from matter"
                title={`Unlink from ${email.matter.caseNumber}`}
                style={{
                  fontSize: 12,
                  color: '#2563EB',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontWeight: 500,
                  padding: 0,
                }}
              >
                Unlink
              </button>
            ) : (
              <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-muted)' }}>
                Linked
              </span>
            )}
          </div>
        ) : email.linkStatus === 'suggested' ? (
          <div style={{
            marginTop: 20,
            padding: '10px 14px',
            background: '#f0fdf4',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: '#166534' }}>
              Suggested matter link
            </span>
            <button
              onClick={onLinkMatter}
              style={{
                fontSize: 12,
                color: '#2563EB',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontWeight: 500,
              }}
            >
              Accept suggestion
            </button>
          </div>
        ) : null}

        {/* Legal workflow action strip — only when matter is linked */}
        {email.matter && (
          <div style={{
            marginTop: 16,
            display: 'flex', flexWrap: 'wrap', gap: 6,
          }}>
            {[
              { label: '✓ Create task', fn: onCreateTask },
              { label: '⚖ Hearing note', fn: onCreateHearingNote },
              { label: '◷ Log time', fn: onLogTime },
              { label: '£ Fee entry', fn: onGenerateFeeEntry },
              { label: '⊕ Save doc', fn: onSaveToDocuments },
            ].map(({ label, fn }) => (
              <button
                key={label}
                onClick={() => fn?.(email.matter!.id, email.matter!.caseName)}
                style={{
                  padding: '5px 11px',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 20,
                  background: 'var(--surface-card)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 12,
                  fontWeight: 500,
                  transition: 'background 0.1s',
                }}
                // Same defect as InboxSettingsHubPage: writing a literal colour inline
                // permanently overrides `background: var(--surface-card)` above, so in dark mode a
                // hovered button stayed white with near-white text on it. Restore to the token.
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface-card)')}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Inline reply / forward */}
      {replyMode && (
        <div style={{ borderTop: '1px solid var(--border-default)', flexShrink: 0, padding: '12px 24px' }}>
          <ReplyPane
            email={email}
            mode={replyMode}
            onClose={() => setReplyMode(null)}
            onSent={() => setReplyMode(null)}
          />
        </div>
      )}
    </div>
  );
}

// ─── Small action button ───────────────────────────────────────

function ActionBtn({
  title, onClick, active, primary, children,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  primary?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-md border px-[11px] py-[5px] text-[12px] font-medium cursor-pointer transition-colors',
        primary
          ? 'border-brand-600 bg-brand-600 text-white hover:bg-brand-700'
          : active
          ? 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-300'
          : 'border-border bg-card text-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}
